import { useState } from 'react';
import { Button, Drawer, Space, message } from 'antd';
import { EyeOutlined, LinkOutlined, GlobalOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api/client';
import Tiptap from './editor/Tiptap';
import CopyUrlField from './publish/CopyUrlField';
import PreviewRelatedNews from './publish/PreviewRelatedNews';
import { useDrawerWidth, useIsMobile } from '../hooks/useIsMobile';

interface Props {
  id: string;
  title: string;
  bodyJson: string | null;
  /** 編輯中挑選的關聯新聞（預覽時接在內文最後，同前台）。 */
  relatedIds: string[];
  /** 已發佈文章的前台正式連結（百分比編碼）；未發佈為 null。 */
  publicUrl: string | null;
}

/** 預覽（後台內）／產生客戶預覽連結／正式發佈連結。發佈與排程見 PublishModePanel。 */
export default function PublishActions({ id, title, bodyJson, relatedIds, publicUrl }: Props) {
  const [preview, setPreview] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const mobile = useIsMobile();
  const previewWidth = useDrawerWidth(880);
  const linkWidth = useDrawerWidth(520);

  const tokenM = useMutation({
    mutationFn: () => api.previewToken(id),
    onSuccess: ({ url }) => setLink(url),
    onError: (e: Error) => message.error(e.message),
  });

  return (
    <div className="flex flex-col gap-3">
      {/* 手機：按鈕滿寬直排，好點 */}
      <Space wrap direction={mobile ? 'vertical' : 'horizontal'} className={mobile ? 'w-full' : ''}>
        <Button block={mobile} icon={<EyeOutlined />} onClick={() => setPreview(true)}>
          後台預覽
        </Button>
        <Button block={mobile} icon={<LinkOutlined />} loading={tokenM.isPending} onClick={() => tokenM.mutate()}>
          產生客戶預覽連結
        </Button>
      </Space>

      {publicUrl && (
        <div className="rounded-lg bg-[#f0f9f0] p-3 ring-1 ring-green-600/15">
          <div className="mb-1 flex items-center gap-1 text-xs font-medium text-green-700">
            <GlobalOutlined />
            正式發佈連結（已上線，可分享）
          </div>
          <CopyUrlField url={publicUrl} />
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-block text-xs text-green-700 hover:underline"
          >
            在新分頁開啟 →
          </a>
        </div>
      )}

      {/* 預覽用 Drawer：從右側滑出，比 Modal 有更多空間讀長文，且不會蓋住整個編輯頁 */}
      <Drawer
        title={`預覽：${title || '未命名'}`}
        open={preview}
        onClose={() => setPreview(false)}
        width={previewWidth}
      >
        <Tiptap initialJson={bodyJson} initialHtml={null} readOnly />
        <PreviewRelatedNews ids={relatedIds} />
      </Drawer>

      <Drawer title="客戶預覽連結" open={!!link} onClose={() => setLink(null)} width={linkWidth}>
        <p className="mb-2 text-gray-500">複製以下連結給客戶確認（7 天內有效）：</p>
        {link && <CopyUrlField url={link} />}
      </Drawer>
    </div>
  );
}
